import React, { useState, useEffect, useRef } from 'react';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Box,
  CircularProgress,
  Autocomplete,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  IconButton,
  Paper,
  Typography,
  Divider,
  alpha,
  Chip,
  useTheme,
  Alert,
} from '@mui/material';
import {
  Add as AddIcon,
  Delete as DeleteIcon,
  Warehouse as WarehouseIcon,
  CalendarToday as CalendarIcon,
  Inventory2 as InventoryIcon,
  SwapHoriz as TransferIcon,
} from '@mui/icons-material';
import { useLanguage } from '../../contexts/LanguageContext';
import { toast } from 'react-toastify';
import {
  WarehouseTransfer,
  CreateWarehouseTransferDto,
  UpdateWarehouseTransferDto,
} from '../../types/warehouseTransfers.types';
import { warehouseTransfersService } from '../../services/warehouseTransfers.service';
import { warehousesService } from '../../services/warehouses.service';
import { itemsService, itemUnitsService, unitsService } from '../../services/items.service';
import { Warehouse } from '../../types/warehouses.types';
import { Item } from '../../types/items.types';
import { useFiscalDateRange } from '../../hooks/useFiscalDateRange';
import { useGridEnterNavigation } from '../../hooks/useGridEnterNavigation';
import { useSelector } from 'react-redux';
import { RootState } from '../../store';

const translations = {
  en: {
    addWarehouseTransfer: 'Add Warehouse Transfer Request',
    editWarehouseTransfer: 'Edit Warehouse Transfer',
    transferNumber: 'Transfer Number',
    transferDate: 'Transfer Date',
    fromWarehouse: 'From Warehouse (Source)',
    toWarehouse: 'To Warehouse (Requester)',
    details: 'Transfer Details',
    item: 'Item',
    unit: 'Unit',
    requestedQty: 'Requested Qty',
    addLine: 'Add Line',
    save: 'Save',
    cancel: 'Cancel',
    required: 'Required',
    notes: 'Notes',
    selectFromWarehouse: 'Select Source Warehouse',
    selectToWarehouse: 'Select Destination Warehouse',
    selectItem: 'Select Item',
    transferInfo: 'Transfer Information',
    pendingNote: 'This request will be sent to the source warehouse keeper for approval.',
    stockWarning: 'Available stock is shown for reference. Source warehouse keeper will verify during approval.',
    available: 'Available',
    successCreate: 'Transfer request created successfully',
    successUpdate: 'Transfer updated successfully',
  },
  ar: {
    addWarehouseTransfer: 'إضافة طلب تحويل بين مخازن',
    editWarehouseTransfer: 'تعديل تحويل بين مخازن',
    transferNumber: 'رقم التحويل',
    transferDate: 'تاريخ التحويل',
    fromWarehouse: 'من المخزن (المصدر)',
    toWarehouse: 'إلى المخزن (الطالب)',
    details: 'تفاصيل التحويل',
    item: 'الصنف',
    unit: 'الوحدة',
    requestedQty: 'الكمية المطلوبة',
    addLine: 'إضافة سطر',
    save: 'حفظ',
    cancel: 'إلغاء',
    required: 'مطلوب',
    notes: 'ملاحظات',
    selectFromWarehouse: 'اختر المخزن المصدر',
    selectToWarehouse: 'اختر المخزن الوجهة',
    selectItem: 'اختر الصنف',
    transferInfo: 'معلومات التحويل',
    pendingNote: 'سيتم إرسال هذا الطلب إلى أمين المخزن المصدر للموافقة.',
    stockWarning: 'الكمية المتوفرة للمعاينة فقط. أمين المخزن المصدر سيتحقق عند الموافقة.',
    available: 'المتوفر',
    successCreate: 'تم إنشاء طلب التحويل بنجاح',
    successUpdate: 'تم تعديل التحويل بنجاح',
  },
};

interface WarehouseTransferDialogProps {
  open: boolean;
  onClose: () => void;
  warehouseTransfer?: WarehouseTransfer;
  onSave: () => void;
}

interface DetailLine {
  id: string;
  itemId: number | null;
  item: Item | null;
  unitId: number | null;
  unit: any | null;
  availableUnits: any[];
  requestedQuantity: number;
  availableQuantity: number | null;
  notes: string;
}

const WarehouseTransferDialog: React.FC<WarehouseTransferDialogProps> = ({
  open,
  onClose,
  warehouseTransfer,
  onSave,
}) => {
  const { language } = useLanguage();
  const t = translations[language];
  const isRTL = language === 'ar';
  const theme = useTheme();
  const isDark = theme.palette.mode === 'dark';
  const { minDate, maxDate } = useFiscalDateRange();
  const isUserSuperAdmin = useSelector((state: RootState) => state.auth.user?.isSuperAdmin) ?? false;
  const isEditMode = !!warehouseTransfer;
  const dateLocked = isEditMode && !isUserSuperAdmin;

  const gridRef = useRef<HTMLDivElement>(null);
  useGridEnterNavigation(gridRef);

  const [formData, setFormData] = useState({
    transferNumber: '',
    transferDate: new Date().toISOString().split('T')[0],
    fromWarehouseId: null as number | null,
    toWarehouseId: null as number | null,
    notes: '',
  });

  const [warehouses, setWarehouses] = useState<Warehouse[]>([]);
  // ✅ كل المخازن (بدون فلترة) — للقائمة المنسدلة FromWarehouse (المصدر)
  const [allWarehouses, setAllWarehouses] = useState<Warehouse[]>([]);
  const [items, setItems] = useState<Item[]>([]);

  const [selectedFromWarehouse, setSelectedFromWarehouse] = useState<Warehouse | null>(null);
  const [selectedToWarehouse, setSelectedToWarehouse] = useState<Warehouse | null>(null);

  const [detailLines, setDetailLines] = useState<DetailLine[]>([]);
  const [loading, setLoading] = useState(false);
  const [errors, setErrors] = useState<Record<string, string>>({});

  // ✅ معرفة مخازن المستخدم الحالي من التوكن
  const [userKeeperWarehouseIds, setUserKeeperWarehouseIds] = useState<number[]>([]);
  const [isSuperAdmin, setIsSuperAdmin] = useState(false);

  useEffect(() => {
    if (open) {
      loadMasterData();
    }
  }, [open]);

  useEffect(() => {
    if (open && warehouses.length > 0 && items.length > 0) {
      if (warehouseTransfer) {
        loadWarehouseTransfer();
      } else {
        loadNewTransferNumber();
        autoSetRequesterWarehouse();
        addNewLine();
      }
    }
  }, [open, warehouseTransfer, warehouses, items]);

  // Reload available stock when From Warehouse changes
  useEffect(() => {
    
    if (selectedFromWarehouse && detailLines.length > 0) {
      const reloadAllStock = async () => {
        for (const line of detailLines) {
          if (line.itemId && line.unitId) {
            try {
              const stockInfo = await warehouseTransfersService.getAvailableStock(
                line.itemId, 
                line.unitId, 
                selectedFromWarehouse.warehouseId
              );
              updateLine(line.id, 'availableQuantity', stockInfo.availableQuantity || 0);
            } catch (error) {
              console.error(`❌ Failed to load stock for line ${line.id}:`, error);
              updateLine(line.id, 'availableQuantity', 0);
            }
          }
        }
      };
      reloadAllStock();
    } else if (!selectedFromWarehouse) {
      setDetailLines(prev => 
        prev.map(line => ({ ...line, availableQuantity: null }))
      );
    }
  }, [selectedFromWarehouse?.warehouseId]);

  const loadMasterData = async () => {
    try {
      const [warehousesData, allWarehousesData, itemsData] = await Promise.all([
        warehousesService.getOperational(),
        warehousesService.getAllForLookupOperational(),
        itemsService.getAll(),
      ]);
      // ✅ قراءة صلاحيات المستخدم من التوكن
      const token = localStorage.getItem('token');
      let keeperIds: number[] = [];
      let superAdmin = false;

      if (token) {
        try {
          const payload = JSON.parse(atob(token.split('.')[1]));

          // هل سوبر أدمن؟
          const roles = payload.role || payload.Role || '';
          superAdmin = (Array.isArray(roles) ? roles : [roles]).some(
            (r: string) => r === 'SuperAdmin' || r === 'Admin'
          );
          setIsSuperAdmin(superAdmin);

          // مخازن أمين المخزن
          const keeperClaims = payload.KeeperWarehouseId;
          keeperIds = Array.isArray(keeperClaims) 
            ? keeperClaims.map(Number) 
            : keeperClaims ? [Number(keeperClaims)] : [];
          setUserKeeperWarehouseIds(keeperIds);

        } catch (e) { console.error('Failed to parse token:', e); }
      }

      // warehouses = مخازن المستخدم (للوجهة ToWarehouse)
      // allWarehouses = كل المخازن بدون فلترة (للمصدر FromWarehouse)
      setWarehouses(warehousesData);
      setAllWarehouses(allWarehousesData);
      setItems(itemsData);
    } catch (error) {
      console.error('❌ Failed to load master data:', error);
    }
  };

  // ✅ SAP Pattern: المنشئ هو المخزن المحتاج (الوجهة)
  // تعيين ToWarehouse تلقائياً لمخزن المستخدم الحالي
  const autoSetRequesterWarehouse = () => {
    if (userKeeperWarehouseIds.length > 0 && warehouses.length > 0) {
      // أمين مخزن — مخزنه هو الوجهة (المحتاج)
      const myWarehouse = warehouses.find(w => w.warehouseId === userKeeperWarehouseIds[0]);
      if (myWarehouse) {
        setSelectedToWarehouse(myWarehouse);
        setFormData(prev => ({ ...prev, toWarehouseId: myWarehouse.warehouseId }));
      }
    }
    // السوبر أدمن يختار يدوياً
  };

  const loadWarehouseTransfer = async () => {
    
    if (!warehouseTransfer) {
      return;
    }


    setFormData({
      transferNumber: warehouseTransfer.transferNumber,
      transferDate: warehouseTransfer.transferDate.split('T')[0],
      fromWarehouseId: warehouseTransfer.fromWarehouseId,
      toWarehouseId: warehouseTransfer.toWarehouseId,
      notes: warehouseTransfer.notes || '',
    });

    const fromWarehouse = warehouses.find(w => w.warehouseId === warehouseTransfer.fromWarehouseId);
    const toWarehouse = warehouses.find(w => w.warehouseId === warehouseTransfer.toWarehouseId);


    setSelectedFromWarehouse(fromWarehouse || null);
    setSelectedToWarehouse(toWarehouse || null);


    // Load details with units and available stock
    const linesPromises = warehouseTransfer.details.map(async (detail) => {
      
      let availableUnits: any[] = [];
      let selectedUnit = null;
      let availableQty = null;

      try {
        const units = await itemUnitsService.getItemUnits(detail.itemId);
        availableUnits = units;
        selectedUnit = units.find(u => u.unitId === detail.unitId) || null;
        
        // Fetch available stock from source warehouse
        if (fromWarehouse) {
          try {
            const stockInfo = await warehouseTransfersService.getAvailableStock(
              detail.itemId, 
              detail.unitId, 
              fromWarehouse.warehouseId
            );
            availableQty = stockInfo.availableQuantity || 0;
          } catch (error) {
            console.error(`    ❌ Failed to load stock for item ${detail.itemId}:`, error);
            availableQty = 0;
          }
        }
      } catch (error) {
        console.error(`    ❌ Failed to load units for item ${detail.itemId}:`, error);
      }

      return {
        id: `${detail.transferDetailId}`,
        itemId: detail.itemId,
        item: {
          itemId: detail.itemId,
          itemCode: detail.itemCode,
          itemName: detail.itemName,
          itemNameAr: detail.itemNameAr,
        } as Item,
        unitId: detail.unitId,
        unit: selectedUnit,
        availableUnits: availableUnits,
        requestedQuantity: detail.requestedQuantity,
        availableQuantity: availableQty,
        notes: detail.notes || '',
      };
    });

    const lines = await Promise.all(linesPromises);
    setDetailLines(lines);
  };

  const loadNewTransferNumber = async () => {
    try {
      const transferNumber = await warehouseTransfersService.generateTransferNumber();
      setFormData((prev) => ({ ...prev, transferNumber }));
    } catch (error) {
      console.error('Failed to generate transfer number:', error);
    }
  };

  const addNewLine = () => {
    const newLine: DetailLine = {
      id: Date.now().toString(),
      itemId: null,
      item: null,
      unitId: null,
      unit: null,
      availableUnits: [],
      requestedQuantity: 1,
      availableQuantity: null,
      notes: '',
    };
    setDetailLines((prev) => [...prev, newLine]);
  };

  const removeLine = (id: string) => {
    setDetailLines((prev) => prev.filter((line) => line.id !== id));
  };

  const updateLine = (id: string, field: keyof DetailLine, value: any) => {
    setDetailLines((prev) =>
      prev.map((line) => (line.id === id ? { ...line, [field]: value } : line))
    );
  };

  const fetchAvailableStock = async (lineId: string, itemId: number, unitId: number, warehouseId: number) => {
    try {
      const stockInfo = await warehouseTransfersService.getAvailableStock(itemId, unitId, warehouseId);
      updateLine(lineId, 'availableQuantity', stockInfo.availableQuantity || 0);
    } catch (error) {
      console.error('❌ Failed to fetch available stock:', error);
      updateLine(lineId, 'availableQuantity', 0);
    }
  };

  const handleUnitChange = async (lineId: string, newUnit: any | null) => {
    updateLine(lineId, 'unit', newUnit);
    updateLine(lineId, 'unitId', newUnit?.unitId || null);
    
    // Fetch available stock for new unit
    const line = detailLines.find(l => l.id === lineId);
    if (newUnit && line?.itemId && selectedFromWarehouse) {
      await fetchAvailableStock(lineId, line.itemId, newUnit.unitId, selectedFromWarehouse.warehouseId);
    } else {
      updateLine(lineId, 'availableQuantity', null);
    }
  };

  const handleItemChange = async (lineId: string, newItem: Item | null) => {
    
    if (!newItem) {
      updateLine(lineId, 'item', null);
      updateLine(lineId, 'itemId', null);
      updateLine(lineId, 'availableUnits', []);
      updateLine(lineId, 'unit', null);
      updateLine(lineId, 'unitId', null);
      updateLine(lineId, 'availableQuantity', null);
      return;
    }

    // Update item
    updateLine(lineId, 'item', newItem);
    updateLine(lineId, 'itemId', newItem.itemId);

    // Fetch units for this item
    try {
      let units = await itemUnitsService.getItemUnits(newItem.itemId);
      
      // ✅ CRITICAL FIX: Check if base unit exists in ItemUnits
      const hasBaseUnit = units.some(u => u.unitId === newItem.baseUnitId);
      
      if (!hasBaseUnit && newItem.baseUnitId) {
        try {
          const allUnits = await unitsService.getAll();
          const baseUnit = allUnits.find(u => u.unitId === newItem.baseUnitId);
          
          if (baseUnit) {
            const baseUnitAsItemUnit = {
              itemUnitId: 0,
              itemId: newItem.itemId,
              unitId: baseUnit.unitId,
              unitName: baseUnit.unitName,
              unitNameAr: baseUnit.unitNameAr,
              unitCode: baseUnit.unitCode,
              conversionFactor: 1,
              defaultSalePrice: 0,
              defaultPurchasePrice: 0,
              isDefaultSale: true,
              isDefaultPurchase: true,
            };
            
            units = [baseUnitAsItemUnit, ...units];
          }
        } catch (error) {
          console.error('❌ Failed to load base unit from Units:', error);
        }
      }
      
      if (units.length > 0) {
        // PRIORITY 1: Find base unit
        let defaultUnit = units.find(u => u.unitId === newItem.baseUnitId);
        
        // PRIORITY 2: default purchase unit
        if (!defaultUnit) {
          defaultUnit = units.find(u => u.isDefaultPurchase);
        }
        
        // PRIORITY 3: first unit
        if (!defaultUnit) {
          defaultUnit = units[0];
        }
        
        
        updateLine(lineId, 'availableUnits', units);
        updateLine(lineId, 'unit', defaultUnit);
        updateLine(lineId, 'unitId', defaultUnit.unitId);
        
        // Fetch available stock for selected unit from source warehouse
        if (selectedFromWarehouse) {
          await fetchAvailableStock(lineId, newItem.itemId, defaultUnit.unitId, selectedFromWarehouse.warehouseId);
        }
      } else {
        // No ItemUnits at all - use BaseUnit from all units
        try {
          const allUnits = await unitsService.getAll();
          const baseUnit = allUnits.find(u => u.unitId === newItem.baseUnitId);
          
          if (baseUnit) {
            const baseUnitAsItemUnit = {
              itemUnitId: 0,
              itemId: newItem.itemId,
              unitId: baseUnit.unitId,
              unitName: baseUnit.unitName,
              unitNameAr: baseUnit.unitNameAr,
              unitCode: baseUnit.unitCode,
              conversionFactor: 1,
              defaultSalePrice: 0,
              defaultPurchasePrice: 0,
              isDefaultSale: true,
              isDefaultPurchase: true,
            };
            
            updateLine(lineId, 'availableUnits', [baseUnitAsItemUnit]);
            updateLine(lineId, 'unit', baseUnitAsItemUnit);
            updateLine(lineId, 'unitId', baseUnit.unitId);
            
            if (selectedFromWarehouse) {
              await fetchAvailableStock(lineId, newItem.itemId, baseUnit.unitId, selectedFromWarehouse.warehouseId);
            }
          } else {
            console.error('❌ Base unit not found!');
            updateLine(lineId, 'availableUnits', []);
            updateLine(lineId, 'unit', null);
            updateLine(lineId, 'unitId', null);
            updateLine(lineId, 'availableQuantity', null);
          }
        } catch (error) {
          console.error('❌ Failed to load base unit:', error);
          updateLine(lineId, 'availableUnits', []);
          updateLine(lineId, 'unit', null);
          updateLine(lineId, 'unitId', null);
          updateLine(lineId, 'availableQuantity', null);
        }
      }
    } catch (error) {
      console.error('❌ Failed to load units:', error);
      updateLine(lineId, 'availableUnits', []);
      updateLine(lineId, 'unit', null);
      updateLine(lineId, 'unitId', null);
      updateLine(lineId, 'availableQuantity', null);
    }
  };

  const validate = (): boolean => {
    const newErrors: Record<string, string> = {};

    if (!formData.fromWarehouseId) {
      newErrors.fromWarehouseId = t.required;
    }
    if (!formData.toWarehouseId) {
      newErrors.toWarehouseId = t.required;
    }
    if (formData.fromWarehouseId && formData.toWarehouseId && formData.fromWarehouseId === formData.toWarehouseId) {
      newErrors.toWarehouseId = isRTL ? 'المصدر والوجهة يجب أن يختلفا' : 'Source and destination must be different';
    }
    if (detailLines.length === 0) {
      newErrors.details = isRTL ? 'يجب إضافة سطر واحد على الأقل' : 'At least one detail line is required';
    }
    detailLines.forEach((line, index) => {
      if (!line.itemId) {
        newErrors[`item_${index}`] = t.required;
      }
      if (!line.unitId) {
        newErrors[`unit_${index}`] = t.required;
      }
      if (line.requestedQuantity <= 0) {
        newErrors[`quantity_${index}`] = isRTL ? 'يجب أن تكون أكبر من صفر' : 'Must be greater than 0';
      }
      // ✅ SAP Pattern: عند الإنشاء (Pending) لا نمنع — تحذير فقط
      // الفحص الفعلي يتم عند الشحن (Ship) في الباكند
      // لكن عند التعديل بحالة Draft (بعد الموافقة) نمنع
      if (warehouseTransfer && warehouseTransfer.status === 'Draft') {
        if (line.availableQuantity !== null && line.requestedQuantity > line.availableQuantity) {
          newErrors[`quantity_${index}`] = isRTL 
            ? `الكمية المطلوبة (${line.requestedQuantity}) أكبر من المتوفر (${line.availableQuantity})`
            : `Requested quantity (${line.requestedQuantity}) exceeds available stock (${line.availableQuantity})`;
        }
      }
    });

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  // ✅ هل توجد كميات تتجاوز المتوفر؟ (تحذير فقط للطلبات الجديدة)
  const hasStockWarnings = !warehouseTransfer && detailLines.some(
    line => line.availableQuantity !== null && line.requestedQuantity > line.availableQuantity
  );

  const handleSubmit = async () => {
    if (!validate()) return;

    setLoading(true);
    try {
      if (warehouseTransfer) {
        const updateDto: UpdateWarehouseTransferDto = {
          transferId: warehouseTransfer.transferId,
          transferDate: formData.transferDate,
          fromWarehouseId: formData.fromWarehouseId!,
          toWarehouseId: formData.toWarehouseId!,
          status: warehouseTransfer.status,
          notes: formData.notes || undefined,
          details: detailLines.map((line, index) => ({
            transferDetailId: (() => { const p = parseInt(line.id); return !isNaN(p) && p > 0 && p < 2147483647 ? p : 0; })(),
            lineNumber: index + 1,
            itemId: line.itemId!,
            unitId: line.unitId!,
            requestedQuantity: line.requestedQuantity,
            notes: line.notes || undefined,
          })),
        };
        await warehouseTransfersService.update(warehouseTransfer.transferId, updateDto);
        toast.success(t.successUpdate);
      } else {
        const createDto: CreateWarehouseTransferDto = {
          transferNumber: formData.transferNumber,
          transferDate: formData.transferDate,
          fromWarehouseId: formData.fromWarehouseId!,
          toWarehouseId: formData.toWarehouseId!,
          notes: formData.notes || undefined,
          details: detailLines.map((line, index) => ({
            lineNumber: index + 1,
            itemId: line.itemId!,
            unitId: line.unitId!,
            requestedQuantity: line.requestedQuantity,
            notes: line.notes || undefined,
          })),
        };
        await warehouseTransfersService.create(createDto);
        toast.success(t.successCreate);
      }
      onSave();
      handleClose();
    } catch (error: any) {
      console.error('Failed to save warehouse transfer:', error);
      if (error.response?.data?.errors) {
        setErrors(error.response.data.errors);
      }
      toast.error(error.response?.data?.message || error.message || (isRTL ? 'فشل حفظ التحويل' : 'Failed to save transfer'));
    } finally {
      setLoading(false);
    }
  };

  const handleClose = () => {
    setFormData({
      transferNumber: '',
      transferDate: new Date().toISOString().split('T')[0],
      fromWarehouseId: null,
      toWarehouseId: null,
      notes: '',
    });
    setDetailLines([]);
    setSelectedFromWarehouse(null);
    setSelectedToWarehouse(null);
    setErrors({});
    onClose();
  };

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      maxWidth="lg"
      fullWidth
      dir={isRTL ? 'rtl' : 'ltr'}
      PaperProps={{
        sx: {
          minHeight: '80vh',
          maxHeight: '95vh',
          borderRadius: 3,
          bgcolor: 'background.default',
        }
      }}
    >
      <DialogTitle 
        sx={{ 
          background: isDark
            ? 'linear-gradient(135deg, #4a148c 0%, #6a1b9a 100%)'
            : 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
          color: 'white',
          py: 3,
          display: 'flex',
          alignItems: 'center',
          gap: 2,
        }}
      >
        <TransferIcon sx={{ fontSize: 36 }} />
        <Box component="span" sx={{ fontWeight: 700, fontSize: '1.5rem' }}>
          {warehouseTransfer ? t.editWarehouseTransfer : t.addWarehouseTransfer}
        </Box>
      </DialogTitle>

      <DialogContent sx={{ px: 3, py: 3 }}>
        {/* ✅ تنبيه: الطلب سيُرسل لأمين المخزن المصدر */}
        {!warehouseTransfer && (
          <Alert severity="info" sx={{ mb: 2, borderRadius: 2 }}>
            {t.pendingNote}
          </Alert>
        )}

        {/* Transfer Info Card */}
        <Paper 
          elevation={3}
          sx={{ 
            p: 3, 
            mb: 3,
            background: isDark 
              ? 'linear-gradient(135deg, #4a148c 0%, #6a1b9a 100%)'
              : 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
            borderRadius: 2,
          }}
        >
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
            <InventoryIcon sx={{ color: 'white' }} />
            <Typography variant="h6" sx={{ color: 'white', fontWeight: 600 }}>
              {t.transferInfo}
            </Typography>
          </Box>

          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
            {/* Row 1: Transfer Number + Date */}
            <Box sx={{ display: 'flex', gap: 2, flexDirection: { xs: 'column', sm: 'row' } }}>
              <Box sx={{ flex: 1 }}>
                <TextField
                  fullWidth
                  label={t.transferNumber}
                  value={formData.transferNumber}
                  disabled
                  error={!!errors.transferNumber}
                  helperText={errors.transferNumber}
                  sx={{
                    backgroundColor: isDark ? alpha('#ffffff', 0.1) : 'rgba(255, 255, 255, 0.9)',
                    borderRadius: 1,
                    '& .MuiOutlinedInput-root': {
                      '& fieldset': { borderColor: 'transparent' },
                    }
                  }}
                  InputProps={{
                    startAdornment: <CalendarIcon sx={{ mr: 1, color: 'primary.main' }} />
                  }}
                />
              </Box>

              <Box sx={{ flex: 1 }}>
                <LocalizationProvider dateAdapter={AdapterDateFns}>
                  <DatePicker
                    label={t.transferDate}
                    value={formData.transferDate ? new Date(formData.transferDate) : null}
                    onChange={(date) => setFormData({ ...formData, transferDate: date ? `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}-${String(date.getDate()).padStart(2, '0')}` : '' })}
                    format="dd/MM/yyyy"
                    views={['year', 'month', 'day']}
                    openTo="day"
                    minDate={minDate}
                    maxDate={maxDate}
                    disabled={dateLocked}
                    slotProps={{ textField: { size: 'small', fullWidth: true, error: !!errors.transferDate, helperText: errors.transferDate, sx: { backgroundColor: isDark ? alpha('#ffffff', 0.1) : 'rgba(255, 255, 255, 0.9)', borderRadius: 1, '& .MuiOutlinedInput-root': { '& fieldset': { borderColor: 'transparent' } } } } }}
                  />
                </LocalizationProvider>
              </Box>
            </Box>

            {/* Row 2: Warehouses — المصدر (FromWarehouse) ← الوجهة/الطالب (ToWarehouse) */}
            <Box sx={{ display: 'flex', gap: 2, flexDirection: { xs: 'column', sm: 'row' }, alignItems: 'center' }}>
              <Box sx={{ flex: 1 }}>
                <Autocomplete
                  options={allWarehouses.filter(w => w.warehouseId !== formData.toWarehouseId)}
                  getOptionLabel={(option) => (isRTL ? option.warehouseNameAr : option.warehouseName)}
                  value={selectedFromWarehouse}
                  onChange={(_, newValue) => {
                    setSelectedFromWarehouse(newValue);
                    setFormData({ ...formData, fromWarehouseId: newValue?.warehouseId || null });
                  }}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      label={t.fromWarehouse}
                      required
                      error={!!errors.fromWarehouseId}
                      helperText={errors.fromWarehouseId}
                      sx={{
                        backgroundColor: isDark ? alpha('#ffffff', 0.1) : 'rgba(255, 255, 255, 0.9)',
                        borderRadius: 1,
                        '& .MuiOutlinedInput-root': {
                          '& fieldset': { borderColor: 'transparent' },
                        }
                      }}
                      InputProps={{
                        ...params.InputProps,
                        startAdornment: (
                          <>
                            <WarehouseIcon sx={{ mr: 1, color: 'error.main' }} />
                            {params.InputProps.startAdornment}
                          </>
                        )
                      }}
                    />
                  )}
                />
              </Box>

              <TransferIcon 
                sx={{ 
                  fontSize: 40, 
                  color: 'white',
                  display: { xs: 'none', sm: 'block' }
                }} 
              />

              <Box sx={{ flex: 1 }}>
                <Autocomplete
                  options={warehouses.filter(w => w.warehouseId !== formData.fromWarehouseId)}
                  getOptionLabel={(option) => (isRTL ? option.warehouseNameAr : option.warehouseName)}
                  value={selectedToWarehouse}
                  onChange={(_, newValue) => {
                    setSelectedToWarehouse(newValue);
                    setFormData({ ...formData, toWarehouseId: newValue?.warehouseId || null });
                  }}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      label={t.toWarehouse}
                      required
                      error={!!errors.toWarehouseId}
                      helperText={errors.toWarehouseId}
                      sx={{
                        backgroundColor: isDark ? alpha('#ffffff', 0.1) : 'rgba(255, 255, 255, 0.9)',
                        borderRadius: 1,
                        '& .MuiOutlinedInput-root': {
                          '& fieldset': { borderColor: 'transparent' },
                        }
                      }}
                      InputProps={{
                        ...params.InputProps,
                        startAdornment: (
                          <>
                            <WarehouseIcon sx={{ mr: 1, color: 'success.main' }} />
                            {params.InputProps.startAdornment}
                          </>
                        )
                      }}
                    />
                  )}
                />
              </Box>
            </Box>
          </Box>
        </Paper>

        <Divider sx={{ my: 3 }}>
          <Chip 
            label={t.details} 
            sx={{ 
              fontWeight: 700,
              fontSize: '1rem',
              px: 2,
              background: isDark
                ? 'linear-gradient(45deg, #7b1fa2 30%, #9c27b0 90%)'
                : 'linear-gradient(45deg, #9c27b0 30%, #ba68c8 90%)',
              color: 'white'
            }} 
          />
        </Divider>

        {/* ✅ تحذير الكمية — للمعاينة فقط عند الإنشاء */}
        {hasStockWarnings && (
          <Alert severity="warning" sx={{ mb: 2, borderRadius: 2 }}>
            {t.stockWarning}
          </Alert>
        )}

        {/* Details Table */}
        <Box ref={gridRef}>
        <TableContainer
          component={Paper}
          elevation={3}
          sx={{
            mb: 2,
            maxHeight: { xs: 300, sm: 400, md: 500 },
            overflowX: 'auto',
            borderRadius: 2,
            border: '2px solid',
            borderColor: isDark ? alpha('#ba68c8', 0.3) : alpha('#9c27b0', 0.2),
          }}
        >
          <Table size="small" stickyHeader>
            <TableHead>
              <TableRow sx={{ 
                background: isDark
                  ? 'linear-gradient(135deg, #4a148c 0%, #6a1b9a 100%)'
                  : 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
              }}>
                <TableCell sx={{ minWidth: 250, color: 'white', fontWeight: 700 }}>{t.item}</TableCell>
                <TableCell sx={{ minWidth: 120, color: 'white', fontWeight: 700 }}>{t.unit}</TableCell>
                <TableCell sx={{ minWidth: 100, color: 'white', fontWeight: 700 }}>{t.available}</TableCell>
                <TableCell sx={{ minWidth: 120, color: 'white', fontWeight: 700 }}>{t.requestedQty}</TableCell>
                <TableCell sx={{ minWidth: 200, color: 'white', fontWeight: 700 }}>{t.notes}</TableCell>
                <TableCell sx={{ width: 60 }}></TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {detailLines.map((line, index) => (
                <TableRow 
                  key={line.id}
                  sx={{
                    '&:hover': {
                      backgroundColor: isDark ? alpha('#ba68c8', 0.1) : alpha('#9c27b0', 0.05),
                    }
                  }}
                >
                  <TableCell>
                    <Autocomplete
                      options={items}
                      getOptionLabel={(option) => (isRTL ? option.itemNameAr : option.itemName)}
                      value={line.item}
                      onChange={(_, newValue) => handleItemChange(line.id, newValue)}
                      renderInput={(params) => (
                        <TextField
                          {...params}
                          size="small"
                          error={!!errors[`item_${index}`]}
                        />
                      )}
                    />
                  </TableCell>
                  <TableCell>
                    <Autocomplete
                      options={line.availableUnits}
                      getOptionLabel={(option) => (isRTL ? option.unitNameAr : option.unitName)}
                      value={line.unit}
                      onChange={(_, newValue) => handleUnitChange(line.id, newValue)}
                      disabled={!line.item || line.availableUnits.length === 0}
                      renderInput={(params) => (
                        <TextField
                          {...params}
                          size="small"
                          error={!!errors[`unit_${index}`]}
                          placeholder={isRTL ? 'اختر الوحدة' : 'Select Unit'}
                        />
                      )}
                    />
                  </TableCell>
                  <TableCell>
                    <Box sx={{ 
                      display: 'flex', 
                      alignItems: 'center', 
                      gap: 1,
                      px: 1,
                      py: 0.5,
                      borderRadius: 1,
                      backgroundColor: line.availableQuantity !== null 
                        ? (line.availableQuantity > 0 
                            ? (isDark ? alpha('#4caf50', 0.2) : alpha('#4caf50', 0.1))
                            : (isDark ? alpha('#f44336', 0.2) : alpha('#f44336', 0.1)))
                        : 'transparent'
                    }}>
                      {line.availableQuantity !== null ? (
                        <>
                          <Typography 
                            variant="body2" 
                            fontWeight="bold"
                            sx={{ 
                              color: line.availableQuantity > 0 
                                ? (isDark ? '#81c784' : '#2e7d32')
                                : (isDark ? '#e57373' : '#c62828')
                            }}
                          >
                            {line.availableQuantity.toFixed(3)}
                          </Typography>
                          {line.requestedQuantity > line.availableQuantity && (
                            <Typography 
                              variant="caption" 
                              sx={{ color: isDark ? '#ef5350' : '#d32f2f', fontWeight: 'bold' }}
                            >
                              ⚠️
                            </Typography>
                          )}
                        </>
                      ) : (
                        <Typography variant="caption" color="text.secondary">
                          -
                        </Typography>
                      )}
                    </Box>
                  </TableCell>
                  <TableCell>
                    <TextField
                      size="small"
                      fullWidth
                      value={line.requestedQuantity === 0 ? '' : String(line.requestedQuantity)}
                      onChange={(e) => {
                        const v = e.target.value;
                        if (v === '' || /^\d*\.?\d*$/.test(v)) {
                          updateLine(line.id, 'requestedQuantity', v === '' || v === '.' ? 0 : parseFloat(v));
                        }
                      }}
                      error={!!errors[`quantity_${index}`]}
                      inputProps={{ inputMode: 'decimal' }}
                      helperText={errors[`quantity_${index}`]}
                      sx={{
                        // ✅ تحذير بصري فقط عند الإنشاء (Pending) — لا يمنع الحفظ
                        ...(!warehouseTransfer && line.availableQuantity !== null && line.requestedQuantity > line.availableQuantity
                          ? {
                              '& .MuiOutlinedInput-root': {
                                '& fieldset': { borderColor: '#ff9800' },
                              }
                            }
                          : {}
                        )
                      }}
                    />
                  </TableCell>
                  <TableCell>
                    <TextField
                      size="small"
                      fullWidth
                      value={line.notes}
                      onChange={(e) => updateLine(line.id, 'notes', e.target.value)}
                    />
                  </TableCell>
                  <TableCell>
                    <IconButton 
                      size="small" 
                      color="error" 
                      onClick={() => removeLine(line.id)}
                      sx={{
                        '&:hover': {
                          backgroundColor: isDark ? alpha('#f44336', 0.2) : alpha('#f44336', 0.1),
                        }
                      }}
                    >
                      <DeleteIcon />
                    </IconButton>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
        </Box>

        <Button
          startIcon={<AddIcon />}
          onClick={addNewLine}
          variant="contained"
          sx={{
            mb: 3,
            background: isDark
              ? 'linear-gradient(45deg, #7b1fa2 30%, #9c27b0 90%)'
              : 'linear-gradient(45deg, #9c27b0 30%, #ba68c8 90%)',
            boxShadow: '0 3px 5px 2px rgba(156, 39, 176, .3)',
            '&:hover': {
              background: isDark
                ? 'linear-gradient(45deg, #6a1b9a 30%, #7b1fa2 90%)'
                : 'linear-gradient(45deg, #7b1fa2 30%, #9c27b0 90%)',
            }
          }}
          size="large"
        >
          {t.addLine}
        </Button>

        <TextField
          fullWidth
          label={t.notes}
          value={formData.notes}
          onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
          multiline
          rows={3}
          size="medium"
          sx={{
            '& .MuiOutlinedInput-root': {
              borderRadius: 2,
            }
          }}
        />
      </DialogContent>

      <DialogActions 
        sx={{ 
          px: 3, 
          py: 2.5,
          bgcolor: isDark ? alpha('#9c27b0', 0.15) : alpha('#9c27b0', 0.05),
          gap: 2,
        }}
      >
        <Button 
          onClick={handleClose} 
          disabled={loading} 
          size="large"
          variant="outlined"
          sx={{ minWidth: 120 }}
        >
          {t.cancel}
        </Button>
        <Button
          onClick={handleSubmit}
          variant="contained"
          disabled={loading}
          startIcon={loading ? <CircularProgress size={20} color="inherit" /> : null}
          size="large"
          sx={{
            minWidth: 120,
            background: isDark
              ? 'linear-gradient(45deg, #388e3c 30%, #4caf50 90%)'
              : 'linear-gradient(45deg, #4caf50 30%, #81c784 90%)',
            boxShadow: '0 3px 5px 2px rgba(76, 175, 80, .3)',
            '&:hover': {
              background: isDark
                ? 'linear-gradient(45deg, #2e7d32 30%, #388e3c 90%)'
                : 'linear-gradient(45deg, #388e3c 30%, #4caf50 90%)',
            }
          }}
        >
          {t.save}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default WarehouseTransferDialog;